import { z } from 'zod';

import {
  type ChunkContentModel,
  chunkContentModelSchema,
  chunkContentModelToJson,
} from '@/domain/models/chunkContentModel';
import {
  type ContextChunksDataModel,
  contextChunksDataModelSchema,
  contextChunksDataModelToJson,
} from '@/domain/models/contextChunksDataModel';

/** Контекст запроса, который нода прислала перед ответом. */
export interface LLMContextModel {
  readonly type: 'context';
  readonly content: Record<string, unknown>;
}

/** Очередной кусок текста ответа. */
export interface LLMChunkModel {
  readonly type: 'chunk';
  readonly content: ChunkContentModel;
}

/** Источники, на которых построен ответ. */
export interface LLMContextChunksModel {
  readonly type: 'context_chunks';
  readonly data: ContextChunksDataModel;
}

/** Объединение по ключу `type` — `@Freezed(unionKey: 'type')` в Dart. */
export type LLMResponseModel =
  | LLMContextModel
  | LLMChunkModel
  | LLMContextChunksModel;

/**
 * Переименований нет ни в одной ветке — `.transform()` на уровне объекта
 * не нужен, разбор идёт прямо в discriminated union по `type`.
 */
export const llmResponseModelSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('context'),
    // `@Default({})` из Dart: без `content` контекст считается пустым.
    content: z
      .record(z.string(), z.unknown())
      .nullish()
      .transform((value): Record<string, unknown> => value ?? {}),
  }),
  z.object({
    type: z.literal('chunk'),
    content: chunkContentModelSchema,
  }),
  z.object({
    type: z.literal('context_chunks'),
    data: contextChunksDataModelSchema,
  }),
]);

export function llmResponseModelFromJson(json: unknown): LLMResponseModel {
  return llmResponseModelSchema.parse(json);
}

/**
 * `LLMResponseModel.tryFromJson` — неизвестный `type` или битое тело дают
 * `null`, и SSE-поток продолжает читаться дальше, а не обрывается.
 */
export function llmResponseModelTryFromJson(
  json: unknown,
): LLMResponseModel | null {
  const result = llmResponseModelSchema.safeParse(json);
  return result.success ? result.data : null;
}

export function llmResponseModelToJson(
  model: LLMResponseModel,
): Record<string, unknown> {
  switch (model.type) {
    case 'context':
      return { content: model.content, type: model.type };
    case 'chunk':
      return { content: chunkContentModelToJson(model.content), type: model.type };
    case 'context_chunks':
      return { data: contextChunksDataModelToJson(model.data), type: model.type };
  }
}
